import { stringify } from "querystring";

import { FunctionComponent, PropsWithChildren, useCallback, useMemo } from "react";

import Grid from "@mui/material/Grid";
import { OrganizationRoutes, FormType } from "@simplyk/common";
import { useRouter } from "next/router";

import { DonationFormStepName } from "../../constants/Steppers";
import { getFromSessionStorage, setInSessionStorage } from "../../helpers/sessionStorage";
import { useInitialStatus } from "../../hooks/useInitialStatus";
import { useTranslate } from "../../hooks/useTranslate";

import { DashboardMobilePopup } from "./DashboardMobilePopup";
import { ViewFormLink } from "./ViewFormLink";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const isShareFormPopupClosedSessionStorageKey = "isShareFormPopupClosed";

export const DashboardMobileShareFormPopup: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslate();
  const { pathname } = useRouter();
  const { latestForm } = useInitialStatus();
  const params = stringify({ shouldOpenShareModal: "true" });
  const formShareLink = useMemo(
    () =>
      latestForm?.formType === FormType.DonationForm
        ? `${OrganizationRoutes.DonationFormsEdit.replace(":id", latestForm.id)}&${params}#${DonationFormStepName.AdvancedParameters}`
        : latestForm?.formType === FormType.Ticketing
          ? `${OrganizationRoutes.TicketingEdit.replace(":id", latestForm.id)}&${params}#${DonationFormStepName.AdvancedParameters}`
          : undefined,
    [latestForm?.formType, latestForm?.id, params]
  );
  const allowedPages = [
    OrganizationRoutes.Campaigns.toString(),
    OrganizationRoutes.Payments.toString(),
    OrganizationRoutes.Contacts.toString(),
    OrganizationRoutes.Emails.toString(),
  ];
  const isAllowedPage = allowedPages.includes(pathname);

  const onShareFormPopupClose = useCallback(() => {
    setInSessionStorage?.(isShareFormPopupClosedSessionStorageKey, "true");
  }, []);
  const isShareFormPopupClosed = Boolean(getFromSessionStorage?.(isShareFormPopupClosedSessionStorageKey));

  return (
    <DashboardMobilePopup
      onClose={onShareFormPopupClose}
      shouldDisplay={isAllowedPage && !isShareFormPopupClosed}
      data-test="new-form-modal"
      popupContent={
        <>
          <Grid item xs={12} data-test="new-form-modal">
            <DashboardTypography variant="h6" vibe="neutral100">
              {t("dashboard", "mobilePopup.shareYourForm.title")}
            </DashboardTypography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <DashboardButton
                  vibe="alternate"
                  variant="filled"
                  size="large"
                  href={formShareLink}
                  onClick={onShareFormPopupClose}
                >
                  {t("dashboard", "mobilePopup.shareYourForm.bank.button")}
                </DashboardButton>
              </Grid>
              <Grid item xs={12}>
                <DashboardTypography variant="h6" vibe="neutral100">
                  {t("common", "or")}
                </DashboardTypography>
              </Grid>
              <Grid item xs={12}>
                <ViewFormLink />
              </Grid>
            </Grid>
          </Grid>
        </>
      }
    >
      {children}
    </DashboardMobilePopup>
  );
};
